import { useEffect, useRef } from 'react';

// Detecta sequências de teclas como "g h" (vá pra home) ou combinações
// como "mod+k" (⌘K ou Ctrl+K). Reset automático se o user demora > 800ms
// entre teclas de uma sequência.
//
// Uso:
//   useKeyboard({
//     '?': () => setShowHelp(true),
//     'Escape': () => closeModal(),
//     'mod+k': () => setPaletteOpen(true),
//     'g h': () => navigate('/'),
//     'g p': () => navigate('/produtos'),
//   });
//
// Ignora automaticamente eventos vindos de <input>, <textarea>, <select>
// e elementos com contentEditable — pra não atrapalhar digitação.

const SEQUENCE_WINDOW_MS = 800;

function isTypingTarget(target) {
  if (!target) return false;
  const tag = target.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if (target.isContentEditable) return true;
  return false;
}

function normalizeKey(e) {
  // `Escape`, `/`, `?`, `a`..`z`, `ArrowUp` etc.
  let key = e.key;
  if (key.length === 1) key = key.toLowerCase();
  const parts = [];
  if (e.metaKey || e.ctrlKey) parts.push('mod');
  if (e.shiftKey && key !== '?') parts.push('shift');
  if (e.altKey) parts.push('alt');
  parts.push(key);
  return parts.join('+');
}

export function useKeyboard(shortcuts, { enabled = true } = {}) {
  const ref = useRef({ buffer: '', timer: null, shortcuts });
  ref.current.shortcuts = shortcuts;

  useEffect(() => {
    if (!enabled) return undefined;

    const handler = (e) => {
      if (isTypingTarget(e.target) && e.key !== 'Escape' && !e.metaKey && !e.ctrlKey) {
        return;
      }
      const current = normalizeKey(e);
      const state = ref.current;

      // Match direto: tecla única (ex: `Escape`, `?`, `mod+k`).
      if (state.shortcuts[current]) {
        e.preventDefault();
        state.shortcuts[current](e);
        state.buffer = '';
        return;
      }

      // Sequência: concatena com buffer separado por espaço.
      const candidate = state.buffer ? `${state.buffer} ${current}` : current;
      if (state.shortcuts[candidate]) {
        e.preventDefault();
        state.shortcuts[candidate](e);
        state.buffer = '';
        if (state.timer) clearTimeout(state.timer);
        return;
      }

      // Algum shortcut começa com este prefixo? Guarda no buffer.
      const hasPrefix = Object.keys(state.shortcuts).some((k) => k.startsWith(`${candidate} `));
      if (hasPrefix) {
        state.buffer = candidate;
        if (state.timer) clearTimeout(state.timer);
        state.timer = setTimeout(() => {
          state.buffer = '';
        }, SEQUENCE_WINDOW_MS);
      } else {
        state.buffer = '';
        if (state.timer) clearTimeout(state.timer);
      }
    };

    const captured = ref.current;
    window.addEventListener('keydown', handler);
    return () => {
      window.removeEventListener('keydown', handler);
      if (captured.timer) clearTimeout(captured.timer);
    };
  }, [enabled]);
}

export default useKeyboard;
